/**
 * Utility functions for timezone handling
 */

/**
 * Convert a time from Paris timezone to user's timezone
 * @param hour - Hour in Paris timezone (0-23)
 * @param minute - Minute (0-59)
 * @param userTimezone - User's timezone (e.g., 'Africa/Douala', 'Europe/Paris')
 * @returns Object with converted time and timezone info
 */
export const convertParisTimeToUserTimezone = (
  hour: number,
  minute: number,
  userTimezone: string
) => {
  // Create a date object for today
  const today = new Date();
  const year = today.getFullYear();
  const month = today.getMonth();
  const day = today.getDate();

  // Create a Date object representing the specified time IN PARIS TIMEZONE
  // We do this by creating a fake UTC date and then finding what UTC time
  // would give us our desired Paris time

  // First, create a date with our desired time as if it were UTC
  const desiredTimeAsUTC = new Date(
    Date.UTC(year, month, day, hour, minute, 0)
  );

  // Now find out what this time would be in Paris
  const whatTimeInParis = desiredTimeAsUTC.toLocaleString('sv-SE', {
    timeZone: 'Europe/Paris',
    hour12: false,
  });

  // Parse the result to get the actual hour/minute that would show in Paris
  const [, parisTimePart] = whatTimeInParis.split(' ');
  const [parisHour, parisMinute] = parisTimePart.split(':').map(Number);

  // Calculate the difference between what we want and what we got
  const hourDiff = hour - parisHour;
  const minuteDiff = minute - parisMinute;

  // Adjust our UTC date by this difference to get the correct Paris time
  const correctUTCTime = new Date(
    desiredTimeAsUTC.getTime() + (hourDiff * 60 + minuteDiff) * 60 * 1000
  );

  // Now format this correct UTC time in both Paris and user timezone
  const parisTimeString = correctUTCTime
    .toLocaleString('sv-SE', {
      timeZone: 'Europe/Paris',
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
    })
    .split(' ')[1];

  const userTimeString = correctUTCTime
    .toLocaleString('sv-SE', {
      timeZone: userTimezone,
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
    })
    .split(' ')[1];

  // Get timezone display names using the current date for DST accuracy
  const parisTimezoneName =
    new Intl.DateTimeFormat('fr-FR', {
      timeZone: 'Europe/Paris',
      timeZoneName: 'short',
    })
      .formatToParts(today)
      .find(part => part.type === 'timeZoneName')?.value || 'CET';

  const userTimezoneName =
    new Intl.DateTimeFormat('fr-FR', {
      timeZone: userTimezone,
      timeZoneName: 'short',
    })
      .formatToParts(today)
      .find(part => part.type === 'timeZoneName')?.value || userTimezone;

  return {
    parisTime: parisTimeString,
    userTime: userTimeString,
    parisTimezone: parisTimezoneName,
    userTimezone: userTimezoneName,
    displayText: `${parisTimeString} (${parisTimezoneName}) → ${userTimeString} (${userTimezoneName})`,
  };
};

/**
 * Get user's timezone from their profile or fallback to browser timezone
 * @param userTimezone - User's timezone from profile
 * @returns User's timezone string
 */
export const getUserTimezone = (userTimezone?: string): string => {
  if (userTimezone) {
    return userTimezone;
  }

  // Fallback to browser timezone
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'UTC';
  }
};

/**
 * Get a list of common timezones for display
 */
export const getCommonTimezones = () => {
  return [
    { value: 'Europe/Paris', label: 'Europe/Paris (CET/CEST)' },
    { value: 'Africa/Douala', label: 'Africa/Douala (WAT)' },
    { value: 'Africa/Lagos', label: 'Africa/Lagos (WAT)' },
    { value: 'Africa/Abidjan', label: 'Africa/Abidjan (GMT)' },
    { value: 'America/New_York', label: 'America/New_York (EST/EDT)' },
    { value: 'America/Los_Angeles', label: 'America/Los_Angeles (PST/PDT)' },
    { value: 'Asia/Tokyo', label: 'Asia/Tokyo (JST)' },
    { value: 'UTC', label: 'UTC (GMT)' },
  ];
};
